import { useCallback } from 'react';
import { useEditorStore } from '../stores/editor-store';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';

async function formatExportHtml(html: string): Promise<string> {
  // prettier-plugin-tailwindcss is Node-only; run it in Cloud Functions.
  try {
    const callable = httpsCallable<{ html: string }, { html: string }>(functions, 'formatExportHtml', {
      timeout: 30000,
    });
    const res = await callable({ html });
    const out = res?.data?.html;
    return typeof out === 'string' && out.trim() ? out : html;
  } catch {
    return html;
  }
}

export function useExport() {
  const getExportData = useEditorStore((s) => s.getExportData);

  const buildFullHtml = useCallback(() => {
    const { html, css } = getExportData();
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <script src="https://cdn.tailwindcss.com"></script>
  <style>
${css}
  </style>
  <title>Grace Design</title>
</head>
<body>
${html}
</body>
</html>`;
  }, [getExportData]);

  const copyToClipboard = useCallback(async () => {
    const content = await formatExportHtml(buildFullHtml());
    await navigator.clipboard.writeText(content);
    return true;
  }, [buildFullHtml]);

  const downloadFile = useCallback(async () => {
    const content = await formatExportHtml(buildFullHtml());
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grace-design-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }, [buildFullHtml]);

  return { copyToClipboard, downloadFile };
}
